import type { AxiosRequestConfig } from 'axios';
import { authService } from '@/lib/auth';

import axios from 'axios';
import qs from 'qs';

const baseURL = import.meta.env.DEV ? 'http://localhost:8000' : `https://${import.meta.env.VITE_BACKEND_URL}`;

const axiosInstance = axios.create({
  baseURL,
  paramsSerializer: {
    serialize: params => qs.stringify(params, { arrayFormat: 'repeat' }),
  },
});

/** Token getter for API requests. Set by AuthProvider so API uses the same token as the UI (e.g. from react-oidc-context). */
let apiTokenGetter: () => string | null | Promise<string | null> = () => authService.getToken();

/** Login redirect for 401. Set by AuthProvider so API triggers the same login as the UI. */
let apiLoginRedirect: (() => void) | null = null;

export function setApiAuth(getToken: () => string | null | Promise<string | null>, onUnauthorized?: () => void): void {
  apiTokenGetter = getToken;
  apiLoginRedirect = onUnauthorized ?? null;
}

// add bearer token
axiosInstance.interceptors.request.use(async config => {
  const token = await apiTokenGetter();
  if (!token) throw new Error('No access token found');

  config.headers.Authorization = `Bearer ${token}`;

  return config;
});

axiosInstance.interceptors.response.use(
  // unwrap data
  ({ data }) => data,
  async error => {
    if (error.response?.status === 401) {
      if (apiLoginRedirect) {
        apiLoginRedirect();
      } else {
        await authService.login();
      }
    }

    return Promise.reject(error);
  }
);

export function apiInstance<T>(request: AxiosRequestConfig): Promise<T> {
  // Response interceptor returns response.data; Axios 1.20+ still types the call as AxiosResponse.
  return axiosInstance(request) as Promise<T>;
}
